import { createSelector } from 'reselect';

import * as cachePolicies from 'helpers/cachePolicies';
import AsyncStatus from 'enums/AsyncStatus';
import { routeParamsSelector } from 'ducks/routeSelectors';
import { FETCH_POST, FETCH_POSTS } from 'ducks/actionTypes';



const defaultState = {
	fetchPostStatus: null,
	fetchPostsStatus: null,

	// Keyed by `post.slug`
	posts: {}
};


function postsReducer(state=defaultState, action) {
	switch (action.type) {
		case FETCH_POST:
		{
			const nextState = {
				...state,
				fetchPostStatus: action.payload.requestStatus,
				posts: { ...state.posts }
			};

			if (action.payload.requestStatus === AsyncStatus.SUCCESS) {
				nextState.posts[action.payload.post.slug] = action.payload.post;
			}

			return nextState;
		}

		case FETCH_POSTS:
		{
			const nextState = {
				...state,
				fetchPostsStatus: action.payload.requestStatus,
				posts: { ...state.posts }
			};

			if (action.payload.requestStatus === AsyncStatus.SUCCESS) {
				action.payload.posts.forEach(post => {
					nextState.posts[post.slug] = post;
				});
			}

			return nextState;
		}

		default:
			return state;
	}
}

export default postsReducer;


// Data Helpers
function mapServerPost(post) {
	return {
		id: post.id,
		slug: post.slug,
		date: post.date,
		modified: post.modified,
		authorId: post.author,
		title: post.title.rendered,
		excerptMarkup: post.excerpt.rendered,
		contentMarkdown: post.content.rendered,
		// Impure
		__cacheTime__: Date.now()
	};
}




///////////////////////////////////
//                               //
//            ACTIONS            //
//                               //
///////////////////////////////////

function fetchPostRequest(requestStatus, post) {
	return {
		type: FETCH_POST,
		payload: {
			requestStatus,
			post
		}
	};
}

export function fetchPost() {
	return async function fetchPostThunk(dispatch, getState, api) {

		const state = getState();
		const { slug } = routeParamsSelector(state);

		// Return cached post possible, skipping network request
		const cachedPost = postBySlugSelector(state, slug);
		if (cachedPost && !cachePolicies.post.isStale(cachedPost.__cacheTime__)) {
			return cachedPost;
		}

		dispatch(fetchPostRequest(AsyncStatus.PENDING));

		const req = await fetch(`https://cmiller.tech/wp/wp-json/wp/v2/posts?slug=${slug}`);
		const json = await req.json();
		const post = mapServerPost(json[0]);

		dispatch(fetchPostRequest(AsyncStatus.SUCCESS, post));

		return post;
	};
}




function fetchPostsRequest(requestStatus, posts) {
	return {
		type: FETCH_POSTS,
		payload: {
			requestStatus,
			posts
		}
	};
}

export function fetchPosts() {
	return async function fetchPostsThunk(dispatch, getState, api) {
		dispatch(fetchPostsRequest(AsyncStatus.PENDING));

		const req = await fetch('https://cmiller.tech/wp/wp-json/wp/v2/posts');
		const json = await req.json();
		const posts = json.map(mapServerPost);

		dispatch(fetchPostsRequest(AsyncStatus.SUCCESS, posts));

		return posts;
	};
}




///////////////////////////////////
//                               //
//           SELECTORS           //
//                               //
///////////////////////////////////


const rootSelector = state => state.posts;
const postsSelector = state => rootSelector(state).posts;

// Request statuses
export const fetchPostStatusSelector = state => rootSelector(state).fetchPostStatus;
export const fetchPostsStatusSelector = state => rootSelector(state).fetchPostsStatus;

// Return cached post by slug, or undefined if post doesn't exist.
export const postBySlugSelector = (state, slug) => postsSelector(state)[slug];

// Return array of all stored posts, sorted newest first.
export const postListSelector = createSelector(
	postsSelector,
	posts => Object.values(posts).sort(compareReverseChronological)
);



// Helpers
// ----------------

/**
 * Given two objects with `date` properties, facilitate sorting reverse chronologically.
 * Any parseable date value will work, but we're currently only working with ISO 8601 formatted date strings.
 *
 * @param  {object} a - First object to compare.
 * @param  {object} b - Second object to compare.
 *
 * @return {int}
 */
export const compareReverseChronological = (a, b) => +new Date(b.date) - +new Date(a.date);



// WEBPACK FOOTER //
// ./src/ducks/posts.js